import { useMemo, useState } from "react";
import Modal from "../../../layouts/Modal";
import { ButtonInput } from "../Inputs/ButtonInput";
import { TextInput } from "../Inputs/TextInput";
import useUtilities from "../../../data/useUtilities";
import useNotification from "../../notifications/useNotifications";
import getAddress from "../../../utils/getAddress";
import LoadingWheel from "../LoadingWheel";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import classNames from "../../../utils/classNames";


interface PickAddressProps {
  register: any;
  setValue: any;
  watch: any;
  classes?: string;
  disabled?: boolean;
  rounded?: boolean;
}

export default function PickAddress({ register, setValue, watch, classes, disabled, rounded }: PickAddressProps) {
  const { getAddresses } = useUtilities();
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);

  const postcode = watch("postcode");

  const [line1, line2, line3, line4, locality, town, county, country] = watch([
    "line1", "line2", "line3", "line4", "locality", "town", "county", "country",
  ]);

  const liveAddress = useMemo(() => ({
    line1, line2, line3, line4, locality, town, county, country,
  }), [line1, line2, line3, line4, locality, town, county, country]);

  const formattedLiveAddress = getAddress(liveAddress);

  const handleGetAddresses = () => {
    if (postcode) {
      setIsLoading(true);
      getAddresses.mutate(postcode, {
        onSuccess: (data: any) => {
          console.log(data);
          if (data.data.length !== 0) {
            //Alphanumeric sort the addresses
            setAddresses(data.data.sort((a: any, b: any) => {
              //If there isn't a line 1 put it at the bottom
              return a.line1 ? a.line1?.localeCompare(b.line1, 'en', { numeric: true }) : +1;
            }));
            setOpen(true);
          } else {
            addNotification({
              variant: "error",
              primaryText: "No addresses found",
            });
          }
        },
        onError: (error) => {
          console.error("error", error);
          addNotification({
            variant: "error",
            primaryText: "Could not get addresses",
          });
        },
        onSettled: () => {
          setIsLoading(false);
        }
      });
    }
  };

  const setAddress = (newAddress: any) => {
    setValue("address", newAddress);
    setValue("postcode", postcode);
    setValue("town", newAddress.town || newAddress.city || newAddress.county || "");
    setValue("county", newAddress.county || "");
    setValue("line1", newAddress.line1 || "");
    setValue("line2", newAddress.line2 || "");
    setValue("line3", newAddress.line2 || "");
    setValue("line4", newAddress.line2 || "");
    setValue("country", newAddress.country || "");
  };

  return (
    <>

      <Modal open={open} setOpen={setOpen} width="max-w-4xl">
        <div className="flex flex-col max-h-[80vh] overflow-auto w-full">
          {/* Display all addresses recieved from API */}
          {
            addresses.length > 0 && (
              addresses.map((address, i) => (
                <div
                  key={i}
                  className="flex flex-col w-full p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setAddress(address);
                    setOpen(false);
                  }}
                >
                  {getAddress(address)}
                </div>
              )))}
        </div>
      </Modal>

      <div className={classes}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextInput
            label="Postcode"
            register={register}
            registerName="postcode"
            disabled={disabled}
            rounded={rounded}
          />
          <div className="flex items-center mt-7">
            {isLoading ? (
              <LoadingWheel />
            ) : (
              <ButtonInput
                label="Lookup Postcode"
                isSubmit={false}
                classes="w-40 h-10"
                onClick={() => handleGetAddresses()}
              />
            )}
          </div>

          <div className="flex items-center cursor-pointer select-none col-span-2 gap-x-4" onClick={() => setShowExtra((prev) => !prev)}>
            <ChevronDownIcon
              className={classNames(
                showExtra ? "" : "rotate-180",
                "h-6 w-6 text-gray-400 hover:text-gray-500 transition-transform duration-200 ease-in-out"
              )}
            />

            {formattedLiveAddress}
          </div>

          <div className={
            classNames(
              showExtra ? "max-h-full opacity-100" : "max-h-0 opacity-0",
              "transition-all duration-300 ease-in-out overflow-hidden",
              "border-t border-gray-200",
              "pt-4 col-span-2 grid grid-cols-1 gap-4 sm:grid-cols-2"
            )}>
            <TextInput
              label="Line 1"
              register={register}
              registerName="line1"
              disabled={disabled}
              rounded={rounded}
            />
            <TextInput
              label="Line 2"
              register={register}
              registerName="line2"
              disabled={disabled}
              rounded={rounded}
            />
            <TextInput
              label="Line 3"
              register={register}
              registerName="line3"
              disabled={disabled}
              rounded={rounded}
            />
            <TextInput
              label="Line 4"
              register={register}
              registerName="line4"
              disabled={disabled}
              rounded={rounded}
            />
            <TextInput
              label="Locality"
              register={register}
              registerName="locality"
              disabled={disabled}
              rounded={rounded}
            />
            <TextInput
              label="Town"
              register={register}
              registerName="town"
              disabled={disabled}
              rounded={rounded}
            />
            <TextInput
              label="County"
              register={register}
              registerName="county"
              disabled={disabled}
              rounded={rounded}
            />
          </div>
        </div>
      </div>
    </>
  );
}